import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { UserCredential } from 'firebase/auth';
import { of } from 'rxjs';
import { catchError, concatMap, exhaustMap, map, tap } from 'rxjs/operators';
import { AuthService } from '../../services/auth.service';
import { UserService } from '../../services/user.service';
import { getUserFromSnapshot } from '../../utils/user.util';
import { AuthActions } from '.';

export class AuthBaseEffects {
  private pathOnLogin?: string;
  private pathOnLogout?: string;

  private actions$: Actions;
  private authService: AuthService;
  private userService: UserService;
  private router: Router;

  public login$;
  public loginSuccess$;
  public logout$;
  public rehydrate$;

  constructor(pathOnLogin: string, pathOnLogout: string) {
    this.pathOnLogin = pathOnLogin;
    this.pathOnLogout = pathOnLogout;

    this.actions$ = inject(Actions);
    this.authService = inject(AuthService);
    this.userService = inject(UserService);
    this.router = inject(Router);

    this.login$ = createEffect(() => {
      return this.actions$.pipe(
        ofType(AuthActions.login),
        exhaustMap((credentials) =>
          this.authService.login(credentials.username, credentials.password).pipe(
            map((creds: UserCredential) => creds.user),
            concatMap((user) => this.userService.getUser(user.uid)),
            map((snapshot) => AuthActions.loginSuccess({ user: getUserFromSnapshot(snapshot) })),
            catchError((error) => of(AuthActions.loginFailure({ error })))
          )
        )
      );
    });

    this.loginSuccess$ = createEffect(
      () => {
        return this.actions$.pipe(
          ofType(AuthActions.loginSuccess),
          tap((res) => {
            console.log('login success');
            if (this.pathOnLogin != undefined) {
              this.router.navigate([this.pathOnLogin]);
            }
          })
        );
      },
      { dispatch: false }
    );

    this.logout$ = createEffect(() => {
      return this.actions$.pipe(
        ofType(AuthActions.logout),
        concatMap(() =>
          this.authService.logout().pipe(
            tap(() => {
              if (this.pathOnLogout != undefined) {
                this.router.navigate([this.pathOnLogout]);
              }
            }),
            map(() => AuthActions.logout()),
            map(() => ({ type: 'Logged out' }))
          )
        )
      );
    });

    this.rehydrate$ = createEffect(() => {
      return this.actions$.pipe(
        ofType(AuthActions.rehydrate),
        exhaustMap((action) =>
          this.userService.getUser(action.userId).pipe(
            map((snapshot) =>
              AuthActions.rehydrateSuccess({ user: getUserFromSnapshot(snapshot) })
            ),
            catchError((error) => of(AuthActions.rehydrateFailure()))
          )
        )
      );
    });
  }
}
